/**
 * IncidentRegister — searchable/filterable compliance audit list of every SOS
 * incident (active + resolved). Read-only. Supports CSV export, bulk PDF export
 * and opens any row into the existing IncidentReview (map playback + timeline).
 * Touches no dispatch/ride state.
 */
import React, { useState, useEffect, useCallback } from "react";
import { Search, Download, FileText, Loader2, RefreshCw, Filter, ShieldAlert, X, FileBarChart } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { SafetyDashboard } from "./SafetyDashboard";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB") : "—");

const LEVEL_OPTS = [["", "All levels"], ["assistance", "Assistance"], ["emergency", "Silent Emergency"]];
const STATUS_OPTS = [["", "All statuses"], ["active", "Active"], ["resolved", "Resolved"]];
const EMPTY = { driver: "", date_from: "", date_to: "", level: "", status: "", q: "", repeat: "" };

const Pill = ({ row }) => {
  const emergency = row.level === "emergency";
  const resolved = row.status === "resolved";
  const cls = resolved ? "border-[#10B981] text-[#065F46] bg-[#ECFDF5]"
    : emergency ? "border-[#DC2626] text-[#991B1B] bg-[#FEE2E2]" : "border-[#F59E0B] text-[#92400E] bg-[#FEF3C7]";
  return <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 border rounded ${cls}`}>{row.state}</span>;
};

export const IncidentRegister = ({ onOpenIncident }) => {
  const [filters, setFilters] = useState({ ...EMPTY });
  const [rows, setRows] = useState(null);
  const [exporting, setExporting] = useState("");
  const [drillLabel, setDrillLabel] = useState("");

  const buildQS = (f) => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v && String(v).trim()) p.append(k, String(v).trim()); });
    return p.toString();
  };

  const load = useCallback(async (f) => {
    const active = f || filters;
    setRows(null);
    try {
      const { data } = await api.get(`/admin/sos/register?${buildQS(active)}`);
      setRows(data.rows || []);
    } catch (e) { toast.error(formatApiError(e) || "Failed to load register"); setRows([]); }
  }, [filters]);

  useEffect(() => { load(EMPTY); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyDrill = (partial, label) => {
    const next = { ...EMPTY, ...partial };
    setFilters(next);
    setDrillLabel(label || "");
    load(next);
    document.querySelector('[data-testid="incident-register"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const clearFilters = () => { setFilters({ ...EMPTY }); setDrillLabel(""); load(EMPTY); };
  const hasFilters = Object.values(filters).some((v) => v && String(v).trim());

  const exportFile = async (ext) => {
    setExporting(ext);
    try {
      const { data } = await api.get(`/admin/sos/register.${ext}?${buildQS(filters)}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      if (ext === "pdf") {
        window.open(url, "_blank");
      } else {
        const a = document.createElement("a");
        a.href = url; a.download = "sos-incident-register.csv"; a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      const msg = e?.response?.status === 404 ? "No incidents match the current filters" : formatApiError(e);
      toast.error(msg || "Export failed");
    } finally { setExporting(""); }
  };

  const exportMonthly = async () => {
    setExporting("monthly");
    try {
      const { data } = await api.get(`/admin/sos/monthly-summary.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatApiError(e) || "Could not generate summary"); }
    finally { setExporting(""); }
  };

  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="space-y-3" data-testid="incident-register">
      <SafetyDashboard onDrill={applyDrill} />
      {/* Filters */}
      <div className="border border-border rounded-xl p-3 bg-white">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground mb-2"><Filter className="w-3.5 h-3.5" /> Filters</div>
        <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <input value={filters.driver} onChange={set("driver")} placeholder="Driver" data-testid="register-filter-driver"
            className="border border-border rounded-lg px-2.5 py-1.5 text-sm" />
          <input type="date" value={filters.date_from} onChange={set("date_from")} data-testid="register-filter-from"
            className="border border-border rounded-lg px-2.5 py-1.5 text-sm" />
          <input type="date" value={filters.date_to} onChange={set("date_to")} data-testid="register-filter-to"
            className="border border-border rounded-lg px-2.5 py-1.5 text-sm" />
          <select value={filters.level} onChange={set("level")} data-testid="register-filter-level"
            className="border border-border rounded-lg px-2.5 py-1.5 text-sm bg-white">
            {LEVEL_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={filters.status} onChange={set("status")} data-testid="register-filter-status"
            className="border border-border rounded-lg px-2.5 py-1.5 text-sm bg-white">
            {STATUS_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={filters.q} onChange={set("q")} placeholder="Search notes…" data-testid="register-filter-search"
              className="w-full border border-border rounded-lg pl-7 pr-2 py-1.5 text-sm" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-2.5">
          <button onClick={() => load()} data-testid="register-apply"
            className="text-xs font-semibold bg-[#0A0A0A] text-white rounded-lg px-3 py-1.5 flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> Apply</button>
          {hasFilters && (
            <button onClick={clearFilters} data-testid="register-clear"
              className="text-xs font-semibold border border-border rounded-lg px-3 py-1.5 flex items-center gap-1.5 hover:bg-secondary"><X className="w-3.5 h-3.5" /> Clear filters</button>
          )}
          <button onClick={() => exportFile("csv")} disabled={!!exporting} data-testid="register-export-csv"
            className="text-xs font-semibold border border-border rounded-lg px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-50">
            {exporting === "csv" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Export CSV</button>
          <button onClick={() => exportFile("pdf")} disabled={!!exporting} data-testid="register-export-pdf"
            className="text-xs font-semibold border border-border rounded-lg px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-50">
            {exporting === "pdf" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} Bulk PDF</button>
          <button onClick={exportMonthly} disabled={!!exporting} data-testid="register-export-monthly"
            className="text-xs font-semibold bg-[#065F46] hover:bg-[#064E3B] text-white rounded-lg px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-50">
            {exporting === "monthly" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileBarChart className="w-3.5 h-3.5" />} Monthly Summary</button>
          {rows !== null && <span className="text-xs text-muted-foreground ml-auto" data-testid="register-count">{rows.length} incident{rows.length === 1 ? "" : "s"}</span>}
        </div>
        {drillLabel && (
          <div className="mt-2 text-[11px] bg-[#EEF2FF] text-[#3730A3] border border-[#C7D2FE] rounded-lg px-2.5 py-1.5 flex items-center gap-1.5" data-testid="register-drill-banner">
            <Filter className="w-3.5 h-3.5" /> Filtered by KPI: <b>{drillLabel}</b>
            <button onClick={clearFilters} className="ml-auto underline hover:no-underline" data-testid="register-drill-clear">clear</button>
          </div>
        )}
      </div>

      {/* Table */}
      {rows === null ? (
        <div className="p-8 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading register…</div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-center text-sm text-muted-foreground border border-dashed border-border rounded-xl" data-testid="register-empty">
          No incidents match the current filters.
        </div>
      ) : (
        <div className="border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#0A0A0A] text-white">
                <tr className="text-left">
                  <th className="px-3 py-2 font-semibold">Activated</th>
                  <th className="px-3 py-2 font-semibold">Driver</th>
                  <th className="px-3 py-2 font-semibold">Vehicle</th>
                  <th className="px-3 py-2 font-semibold">Level / State</th>
                  <th className="px-3 py-2 font-semibold">Trip</th>
                  <th className="px-3 py-2 font-semibold">Resolved</th>
                  <th className="px-3 py-2 font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id} data-testid={`register-row-${r.id}`}
                    className={`border-t border-border ${i % 2 ? "bg-[#FAFAFA]" : "bg-white"} hover:bg-secondary`}>
                    <td className="px-3 py-2 whitespace-nowrap">{fmt(r.created_at)}</td>
                    <td className="px-3 py-2 font-semibold flex items-center gap-1"><ShieldAlert className={`w-3.5 h-3.5 ${r.level === "emergency" ? "text-[#DC2626]" : "text-[#B45309]"}`} />{r.driver_name || "—"}</td>
                    <td className="px-3 py-2 text-muted-foreground">{r.vehicle?.make || "—"} · {r.vehicle?.plate || "—"}</td>
                    <td className="px-3 py-2"><Pill row={r} /></td>
                    <td className="px-3 py-2 text-muted-foreground">{r.on_trip ? (r.passenger_name || "On trip") : "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">{r.status === "resolved" ? `${fmt(r.resolved_at)}${r.resolved_by ? ` · ${r.resolved_by}` : ""}` : "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => onOpenIncident(r.id)} data-testid={`register-review-${r.id}`}
                        className="text-xs font-semibold text-white bg-[#0A0A0A] hover:bg-black px-2.5 py-1 rounded-lg">Review</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncidentRegister;
